import React, {useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography
} from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useNavigate, useParams} from "react-router";
import {useIntelligenceRequirement} from '../../../../../firebase/firestore/useIntelligenceRequirement';
import PIRHeader from './PIRHeader';
import PIRList from "./PIRList";
import Timestamp from '../../../../../components/Timestamp';
const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {Authorization: `Bearer ${t}`} : {}; }

const IntelligenceRequirementPage = () => {
    const {irId} = useParams();
    const navigate = useNavigate();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const {data, loading, error} = useIntelligenceRequirement(irId);

    const handleDelete = async () => {
        if (!irId) return;
        setDeleting(true);
        try {
            await fetch(`${_awpBase}/api/intelligence-requests/${encodeURIComponent(irId)}`, {method: 'DELETE', headers: _authH()});
            setConfirmOpen(false);
            navigate('/IR');
        } catch (err) {
            console.error('Failed to delete IR', err);
            alert('Failed to delete IR: ' + (err.message || err));
        } finally {
            setDeleting(false);
        }
    };

    if (loading) return <Stack p={2}>Loading intelligence requirement…</Stack>;
    if (error) return <Stack p={2} sx={{color: 'red'}}>Error loading intelligence
        requirement: {String(error?.message || error)}</Stack>;
    if (!data) return <Stack p={2}>Intelligence requirement not found.</Stack>;

    return (
        <Stack>
            <Stack
                p={1}
                bgcolor={'white'}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
            >
                <Button
                    onClick={() => navigate('/IR')}
                >Back</Button>
                <Button
                    variant="text"
                    onClick={() => setConfirmOpen(true)}
                    startIcon={<DeleteOutlineIcon/>}
                    color="error"
                >
                    Delete
                </Button>
            </Stack>

            <Stack direction={'row'} p={2} spacing={2} alignItems={'flex-start'}>
                <Stack
                    p={2}
                    borderRadius={2}
                    bgcolor={'white'}
                    spacing={2}
                >
                    <Stack spacing={1}>
                        <Typography variant={'h5'}>{data.title || `ID: ${data.id}`}</Typography>
                        <Typography>Priority: {data.priority}</Typography>
                        <Typography>Status: {data.status}</Typography>
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant={'body2'} color={'text.secondary'}>Key Intelligence Questions:</Typography>
                        <Typography sx={{whiteSpace: 'pre-line'}}>{data.fullQuestion}</Typography>
                    </Stack>

                    <Divider/>

                    <PIRHeader
                        title={'Priority Intelligence Requirements'}
                        createLabel={'Create PIR'}
                    />

                    <PIRList/>
                </Stack>

                <Stack
                    p={2}
                    borderRadius={2}
                    bgcolor={'white'}
                    width={320}
                    spacing={2}
                >
                    <Typography variant={'h6'}>Metadata</Typography>
                    <Stack spacing={1}>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Created by:</Typography>
                            <Typography>{data.createdBy}</Typography>
                        </Stack>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Created at:</Typography>
                            <Timestamp component={Typography} value={data.createdAt} fallback={'N/A'}/>
                        </Stack>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Assigned to:</Typography>
                            <Typography>{data.assignedTo || 'N/A'}</Typography>
                        </Stack>
                    </Stack>
                </Stack>

            </Stack>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            This action will permanently delete Intelligence Requirement "{data?.title || data?.id}".
                        </Typography>
                        <Alert severity={'warning'}>This cannot be undone.</Alert>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button
                        color="error"
                        onClick={handleDelete}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default IntelligenceRequirementPage;